/**
 * Extracts a human-readable message from an API error response.
 * The axios response interceptor (src/lib/axios.ts) reads the same body
 * through `messageFromErrorBody`, so the toast and a caller that passes
 * `suppressToast: true` and surfaces the message itself always agree.
 *
 * Server error shape: { message: string, errors?: string | string[] | Record<string, string[]> }.
 * Some refusals put the reason one level down instead, with `message: null`:
 * `{ data: { message, needs_verification, type }, message: null, next }`
 * (e.g. `/account/payment` for a user with no verified mobile number).
 */
export function messageFromErrorBody(data: any): string | null {
  if (!data || typeof data !== "object") return null;

  const firstError = (() => {
    const errs = data?.errors;
    if (!errs) return null;
    if (typeof errs === "string") return errs;
    if (Array.isArray(errs)) return typeof errs[0] === "string" ? errs[0] : null;
    if (typeof errs === "object") {
      const first = Object.values(errs)[0];
      if (typeof first === "string") return first;
      if (Array.isArray(first) && typeof first[0] === "string") return first[0];
    }
    return null;
  })();

  const nested = typeof data?.data?.message === "string" && data.data.message.trim() ? data.data.message.trim() : null;
  return (typeof data.message === "string" && data.message.trim() ? data.message : null) || firstError || nested;
}

export function getErrorMessage(error: unknown, fallback: string): string {
  return messageFromErrorBody((error as any)?.response?.data) || fallback;
}

/** The refusal's `next` step, when the backend names one (e.g. "verify_mobile_for_payment"). */
export function errorNextStep(error: unknown): string | null {
  const next = (error as any)?.response?.data?.next;
  return typeof next === "string" && next.trim() ? next.trim() : null;
}
